import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import { mapApiUsersToUserRows } from "design-components";
import { SETTINGS_USERS_LIST_PAGE_SIZE, settingsUsersInitialState } from "./settings-users.models";
import {
  createSettingsUser,
  deleteSettingsUsers,
  fetchSettingsUsers,
  reactivateSettingsUsers,
  resendInviteSettingsUsers,
  updateSettingsUser,
} from "./settings-users.thunk";

const settingsUsersSlice = createSlice({
  name: "settingsUsers",
  initialState: settingsUsersInitialState,
  reducers: {
    setUsersListSearch: (state, action: PayloadAction<string>) => {
      const t = action.payload.trim();
      state.firstName = t;
      state.lastName = t;
      state.emailId = t;
      state.fromIndex = 0;
      state.toIndex = SETTINGS_USERS_LIST_PAGE_SIZE;
    },
    setUsersListSort: (
      state,
      action: PayloadAction<{ sortingOrder: "asc" | "desc"; sortingField?: string }>,
    ) => {
      state.sortingOrder = action.payload.sortingOrder;
      if (action.payload.sortingField) {
        state.sortingField = action.payload.sortingField;
      }
      state.fromIndex = 0;
      state.toIndex = SETTINGS_USERS_LIST_PAGE_SIZE;
    },
    setUsersListRange: (state, action: PayloadAction<{ fromIndex: number; toIndex: number }>) => {
      state.fromIndex = action.payload.fromIndex;
      state.toIndex = action.payload.toIndex;
    },
    setUsersListFilters: (
      state,
      action: PayloadAction<{ listFilterStatus: string[]; listFilterRoleName: string[] }>,
    ) => {
      state.listFilterStatus = [...action.payload.listFilterStatus];
      state.listFilterRoleName = [...action.payload.listFilterRoleName];
      state.fromIndex = 0;
      state.toIndex = SETTINGS_USERS_LIST_PAGE_SIZE;
    },
    clearUsersListFilters: (state) => {
      state.listFilterStatus = [];
      state.listFilterRoleName = [];
      state.fromIndex = 0;
      state.toIndex = SETTINGS_USERS_LIST_PAGE_SIZE;
    },
    clearSettingsUserSubmitState: (state) => {
      state.submitting = false;
      state.submitError = null;
    },
    clearSettingsUsersDeleteState: (state) => {
      state.deleteSubmitting = false;
      state.deleteError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSettingsUsers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSettingsUsers.fulfilled, (state, action) => {
        state.loading = false;
        state.totalUsers = action.payload.data.totalUsers;
        state.sortingField = action.payload.data.sortingField;
        state.sortingOrder = action.payload.data.sortingOrder;
        state.rows = mapApiUsersToUserRows(action.payload.data.users);
      })
      .addCase(fetchSettingsUsers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? "Failed to fetch users";
      })
      .addCase(createSettingsUser.pending, (state) => {
        state.submitting = true;
        state.submitError = null;
      })
      .addCase(createSettingsUser.fulfilled, (state) => {
        state.submitting = false;
        state.submitError = null;
      })
      .addCase(createSettingsUser.rejected, (state, action) => {
        state.submitting = false;
        state.submitError = action.payload ?? "Failed to create user";
      })
      .addCase(updateSettingsUser.pending, (state) => {
        state.submitting = true;
        state.submitError = null;
      })
      .addCase(updateSettingsUser.fulfilled, (state) => {
        state.submitting = false;
        state.submitError = null;
      })
      .addCase(updateSettingsUser.rejected, (state, action) => {
        state.submitting = false;
        state.submitError = action.payload ?? "Failed to update user";
      })
      .addCase(deleteSettingsUsers.pending, (state) => {
        state.deleteSubmitting = true;
        state.deleteError = null;
      })
      .addCase(deleteSettingsUsers.fulfilled, (state) => {
        state.deleteSubmitting = false;
        state.deleteError = null;
      })
      .addCase(deleteSettingsUsers.rejected, (state, action) => {
        state.deleteSubmitting = false;
        state.deleteError = action.payload ?? "Failed to deactivate user";
      })
      .addCase(reactivateSettingsUsers.pending, (state) => {
        state.submitting = true;
        state.submitError = null;
      })
      .addCase(reactivateSettingsUsers.fulfilled, (state) => {
        state.submitting = false;
        state.submitError = null;
      })
      .addCase(reactivateSettingsUsers.rejected, (state, action) => {
        state.submitting = false;
        state.submitError = action.payload ?? "Failed to reactivate user";
      })
      .addCase(resendInviteSettingsUsers.pending, (state) => {
        state.submitting = true;
        state.submitError = null;
      })
      .addCase(resendInviteSettingsUsers.fulfilled, (state) => {
        state.submitting = false;
        state.submitError = null;
      })
      .addCase(resendInviteSettingsUsers.rejected, (state, action) => {
        state.submitting = false;
        state.submitError = action.payload ?? "Failed to resend invitation";
      });
  },
});

export const {
  setUsersListSearch,
  setUsersListSort,
  setUsersListRange,
  setUsersListFilters,
  clearUsersListFilters,
  clearSettingsUserSubmitState,
  clearSettingsUsersDeleteState,
} = settingsUsersSlice.actions;
export default settingsUsersSlice.reducer;
